import { useEffect, useState } from 'react';
import { FormControl, TextField } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { Entity, stringifyEntityRef } from '@backstage/catalog-model';

/**
 * Custom scaffolder field that selects a Domain entity by its display title.
 *
 * The stock EntityPicker labels the selected value with the full entity ref
 * (e.g. `domain:default/sport-rec`), which reads as noise in the form. This
 * picker shows `metadata.title` (falling back to name) while still writing the
 * full entity ref string to form data, so templates consume it exactly like an
 * EntityPicker value.
 */
export const DomainPicker = ({
  formData,
  onChange,
  rawErrors,
  required,
  schema,
}: any) => {
  const catalogApi = useApi(catalogApiRef);
  const [domains, setDomains] = useState<Entity[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [loadError, setLoadError] = useState<string>('');

  const label = (entity: Entity) =>
    entity.metadata.title ?? entity.metadata.name;

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const { items } = await catalogApi.getEntities({
          filter: { kind: 'Domain' },
          fields: [
            'kind',
            'metadata.name',
            'metadata.namespace',
            'metadata.title',
          ],
        });
        if (!cancelled) {
          setDomains(
            [...items].sort((a, b) => label(a).localeCompare(label(b))),
          );
          setLoading(false);
        }
      } catch (err) {
        if (!cancelled) {
          setLoadError(
            err instanceof Error ? err.message : 'Failed to load domains',
          );
          setLoading(false);
        }
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [catalogApi]);

  const selected =
    domains.find(d => stringifyEntityRef(d) === formData) ?? null;

  return (
    <FormControl fullWidth margin="normal" required={required}>
      <Autocomplete
        value={selected}
        loading={loading}
        options={domains}
        getOptionLabel={label}
        onChange={(_event, value) =>
          onChange(value ? stringifyEntityRef(value) : undefined)
        }
        renderInput={params => (
          <TextField
            {...params}
            label={schema?.title ?? 'Business Unit'}
            variant="outlined"
            required={required}
            error={rawErrors?.length > 0 || !!loadError}
            helperText={loadError || schema?.description}
          />
        )}
      />
    </FormControl>
  );
};
